import type {
  CategoryBudgetStatus,
  CategoryOverviewResponse,
  ReviewQueueItem,
  ReviewReason,
} from '@/types';

/** Pluggy's uncategorized sentinel (backend: PluggyCategoryBusinessRules.UncategorizedCategoryId). */
export const UNCATEGORIZED_CATEGORY_ID = '99999999';

// The parser lives with the shared editor; re-exported here for its longtime importers.
export { parseLimitInput } from '@/components/categories/InlineLimitEditor';

export const STATUS_META: Record<
  CategoryBudgetStatus,
  { label: string; bar: string; text: string }
> = {
  on_track: { label: 'ok', bar: 'bg-success', text: 'text-success-text' },
  warning: { label: 'atenção', bar: 'bg-warning', text: 'text-warning-text' },
  exceeded: { label: 'estourado', bar: 'bg-error', text: 'text-error-text' },
  no_limit: { label: 'sem limite', bar: 'bg-muted', text: 'text-text-tertiary' },
};

export const REASON_LABEL: Record<ReviewReason, string> = {
  no_category: 'sem categoria',
  new_merchant: 'novo estabelecimento',
  user_categorized_before: 'você categorizou antes',
  possible_refund: 'possível reembolso',
  refund_not_arrived: 'reembolso não chegou',
  duplicate_investment: 'investimento duplicado',
};

/** Refund rows have their own actions (link/keep) and stay out of batch confirms. */
export function isRefundReviewReason(reason: ReviewReason): boolean {
  return reason === 'possible_refund' || reason === 'refund_not_arrived';
}

/** Duplicate rows decide a grouping, not a category, so they stay out of batch confirms too. */
export function isDuplicateReviewReason(reason: ReviewReason): boolean {
  return reason === 'duplicate_investment';
}

/** Rows whose decision is a category one — the only ones a batch confirm can act on. */
export function isCategoryReviewReason(reason: ReviewReason): boolean {
  return !isRefundReviewReason(reason) && !isDuplicateReviewReason(reason);
}

/**
 * Queue rows are (transaction, reason) pairs: the same transaction can carry a
 * refund item AND a category item at once, so `id` alone is not a row identity.
 */
export function reviewItemKey(item: Pick<ReviewQueueItem, 'id' | 'reason'>): string {
  return `${item.id}:${item.reason}`;
}

/**
 * A refund queue row anchors the pair by its reason: refund_not_arrived rows ARE
 * the expense (picking the received estorno); possible_refund rows ARE the refund.
 */
export function resolveRefundPair(
  item: Pick<ReviewQueueItem, 'id' | 'reason'>,
  counterpartId: string,
): { expenseTransactionId: string; refundTransactionId: string } {
  return item.reason === 'refund_not_arrived'
    ? { expenseTransactionId: item.id, refundTransactionId: counterpartId }
    : { expenseTransactionId: counterpartId, refundTransactionId: item.id };
}

/** Mirrors backend CategoryBudgetBusinessRules.ComputeStatus — a missing or non-positive limit is an explicit 'no_limit', never 'exceeded'. */
export function computeCategoryBudgetStatus(spent: number, limit: number | null): CategoryBudgetStatus {
  if (limit == null || limit <= 0) return 'no_limit';
  const pct = (spent / limit) * 100;
  if (pct < 80) return 'on_track';
  if (pct <= 100) return 'warning';
  return 'exceeded';
}

/** Backend rounds burn to 1 decimal (CategoryOverviewService) — match it so the optimistic value doesn't flicker on revalidate. */
function computeBurnPercent(spent: number, limit: number | null): number | null {
  if (limit == null || limit <= 0) return null;
  return Math.round((spent / limit) * 1000) / 10;
}

/**
 * A range overview displays limits scaled by budgetMonths (backend `budgetMonthScale`);
 * the editor and the API contract are MONTHLY. Converts a displayed limit back to the
 * monthly value for editor seeding.
 */
export function monthlyLimitFromScaled(scaledLimit: number | null, budgetMonths: number): number | null {
  if (scaledLimit == null || budgetMonths <= 0 || budgetMonths === 1) return scaledLimit;
  return Math.round((scaledLimit / budgetMonths) * 100) / 100;
}

/**
 * Optimistic patch of one limit into a cached CategoryOverviewResponse, so the new
 * value renders before the PUT + revalidate round-trips. `limitAmount` is the MONTHLY
 * value sent to the API; the cached overview carries limits scaled by
 * `overview.budgetMonths` (backend `budgetMonthScale`), so the patch scales the same
 * way. `summary` is intentionally NOT patched (the backend's fold isn't worth
 * mirroring) — the background revalidate corrects it. `rootRef` is only needed when
 * stamping a FIRST limit on a child line, where no childLimits entry exists yet to
 * carry it.
 */
export function applyLimitToOverview(
  overview: CategoryOverviewResponse,
  categoryRef: string,
  limitAmount: number | null,
  rootRef?: string | null,
): CategoryOverviewResponse {
  const scale = overview.budgetMonths > 0 ? overview.budgetMonths : 1;
  const scaledLimit = limitAmount == null ? null : Math.round(limitAmount * scale * 100) / 100;
  if (overview.rows.some((row) => row.categoryRef === categoryRef)) {
    return {
      ...overview,
      rows: overview.rows.map((row) =>
        row.categoryRef === categoryRef
          ? {
              ...row,
              limit: scaledLimit,
              burnPercent: computeBurnPercent(row.spent, scaledLimit),
              status: computeCategoryBudgetStatus(row.spent, scaledLimit),
            }
          : row,
      ),
    };
  }
  if (overview.childLimits.some((child) => child.categoryRef === categoryRef)) {
    return {
      ...overview,
      childLimits:
        scaledLimit == null
          ? overview.childLimits.filter((child) => child.categoryRef !== categoryRef)
          : overview.childLimits.map((child) =>
              child.categoryRef === categoryRef
                ? {
                    ...child,
                    limit: scaledLimit,
                    burnPercent: computeBurnPercent(child.spent, scaledLimit),
                    status: computeCategoryBudgetStatus(child.spent, scaledLimit),
                  }
                : child,
            ),
    };
  }
  if (scaledLimit == null) return overview;
  return {
    ...overview,
    childLimits: [
      ...overview.childLimits,
      // First limit on a child: the overview carries no spend for unlimited children,
      // and its label/icon render from the taxonomy — the blanks are inert and the
      // background revalidate fills the real numbers.
      {
        categoryRef,
        rootRef: rootRef ?? null,
        name: '',
        icon: null,
        spent: 0,
        txCount: 0,
        limit: scaledLimit,
        burnPercent: 0,
        status: 'on_track',
      },
    ],
  };
}

/**
 * Limit editing anchors to a month, so it is only available when the visible period is
 * exactly one calendar month.
 */
export function isSingleCalendarMonth(from: Date, to: Date): boolean {
  if (from.getDate() !== 1) return false;
  if (from.getFullYear() !== to.getFullYear() || from.getMonth() !== to.getMonth()) return false;
  const lastDay = new Date(from.getFullYear(), from.getMonth() + 1, 0).getDate();
  return to.getDate() === lastDay;
}

/**
 * Whether the visible period is a single-month view (flips the "no mês" labels to
 * "no período" when it isn't). The `current_month` preset resolves to 1st → TODAY, so it
 * must count as a single month explicitly — the calendar check would only accept it on
 * the month's last day.
 */
export function isSingleMonthView(period: { preset: string; from: Date; to: Date }): boolean {
  if (period.preset === 'current_month') return true;
  return isSingleCalendarMonth(period.from, period.to);
}

